import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Chip,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  TableSortLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Inventory as InventoryIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import {
  useTSQPeopleList,
  useTSMDeactivatePerson,
  useTSMReactivatePerson,
} from "../../hooks/useTSEntities";
import { ListRequestFilter } from "../../types/common/commonTypes";
import { EmployeeFull } from "../../types/personTypes";
import { paginationDefaults } from "../../config/settingsDefaults";

export const PageEmployeeList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);

  // Local state for UI controls
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "inactive"
  >("active");
  const [searchFilters, setSearchFilters] = useState<ListRequestFilter[][]>([
    [],
  ]);
  const [pagination, setPagination] = useState({
    page: 0,
    size: paginationDefaults.startingRowOption,
  });
  const [error, setError] = useState<string | null>(null);

  // Sorting
  const [orderBy, setOrderBy] = useState<string>("");
  const [order, setOrder] = useState<"asc" | "desc">("asc");

  // TanStack Query hooks
  const {
    data: empsResponse,
    isLoading: empsLoading,
    error: empsError,
    isPlaceholderData,
  } = useTSQPeopleList(
    {
      skip: -1,
      limit: -1,
      active_status: statusFilter,
      filters: searchFilters,
    },
    "employee",
  );

  const deactivateEmpMutation = useTSMDeactivatePerson();
  const reactivateEmpMutation = useTSMReactivatePerson();

  const [employees, setPeople] = useState<EmployeeFull[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);

  useEffect(() => {
    if (empsResponse?.items) {
      setPeople(empsResponse.items as EmployeeFull[]);
      setTotalCount(empsResponse.total);
    }
  }, [empsResponse]);

  // Handle mutations
  const handleDeactivate = async (id: string) => {
    if (!window.confirm(`Are you sure you want to deactivate this employee?`)) {
      return;
    }
    try {
      await deactivateEmpMutation.mutateAsync({ id, person_type: "employee" });
      setError(null);
    } catch (err: any) {
      console.error("Failed to deactivate employee:", err);
      setError(err.response?.data?.detail || "Failed to deactivate employee");
    }
  };

  const handleReactivate = async (id: string) => {
    try {
      await reactivateEmpMutation.mutateAsync({ id, person_type: "employee" });
      setError(null);
    } catch (err: any) {
      console.error("Failed to restore employee:", err);
      setError(err.response?.data?.detail || "Failed to restore employee");
    }
  };

  // Pagination handlers
  const handlePageChange = (event: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPagination((prev) => ({
      ...prev,
      size: parseInt(event.target.value, 10),
      page: 0,
    }));
  };

  // Handle sorting
  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Get sortable value from item
  const getSortValue = (employee: EmployeeFull, property: string): any => {
    switch (property) {
      case "first_name":
        return employee.first_name;
      case "last_name":
        return employee.last_name;
      case "job_title":
        return employee.job_title;
      case "sales_rep":
        return employee.sales_rep;
      case "status":
        return employee.is_record_active ? "active" : "inactive";
      default:
        return "";
    }
  };

  // Filter and sort items (client-side filtering for search and category)
  const filteredAndSortedEmployees = useMemo(() => {
    const filtered = (() => {
      return (employees as EmployeeFull[]).filter((emp) => {
        return (
          searchTerm === "" ||
          (emp.job_title || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          emp.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          emp.last_name.toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
    })();

    if (orderBy) {
      const sorted = [...filtered].sort((a, b) => {
        const aValue = getSortValue(a, orderBy);
        const bValue = getSortValue(b, orderBy);

        if (aValue === bValue) return 0;

        if (order === "asc") {
          return aValue < bValue ? -1 : 1;
        } else {
          return aValue > bValue ? -1 : 1;
        }
      });
      const start = pagination.size * pagination.page;
      const end = start + pagination.size;
      return sorted.slice(start, end);
    } else {
      const start = pagination.size * pagination.page;
      const end = start + pagination.size;
      return filtered.slice(start, end);
    }
  }, [employees, searchTerm, orderBy, order, pagination]);

  // Show loading spinner only on initial load
  if (empsLoading && employees.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          Employees
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(`/people/form?mode=create&type=employee`)}
        >
          {`Add Employee`}
        </Button>
      </Box>

      {/* Error handling */}
      {(error || empsError) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error || empsError?.message || "An error occurred"}
        </Alert>
      )}

      {/* Loading indicator for background updates */}
      {isPlaceholderData && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Loading updated data...
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <TextField
            label="Search"
            placeholder={"Search by first/last name or job title..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
              ),
            }}
            sx={{ minWidth: 300 }}
          />

          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              label="Status"
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Items Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                sortDirection={orderBy === "first_name" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "first_name"}
                  direction={orderBy === "first_name" ? order : "asc"}
                  onClick={() => handleSort("first_name")}
                >
                  First Name
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "last_name" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "last_name"}
                  direction={orderBy === "last_name" ? order : "asc"}
                  onClick={() => handleSort("last_name")}
                >
                  Last Name
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "job_title" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "job_title"}
                  direction={orderBy === "job_title" ? order : "asc"}
                  onClick={() => handleSort("job_title")}
                >
                  Job Title
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "sales_rep" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "sales_rep"}
                  direction={orderBy === "sales_rep" ? order : "asc"}
                  onClick={() => handleSort("sales_rep")}
                >
                  Sales Rep
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === "status" ? order : false}>
                <TableSortLabel
                  active={orderBy === "status"}
                  direction={orderBy === "status" ? order : "asc"}
                  onClick={() => handleSort("status")}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {empsLoading && employees.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : filteredAndSortedEmployees.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {`No Employees Found`}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredAndSortedEmployees.map((emp) => {
                return (
                  <TableRow key={emp.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {emp.first_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {emp.last_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {emp.job_title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {emp.sales_rep ? (
                        <CheckCircleIcon color="primary" />
                      ) : (
                        <CancelIcon color="error" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={emp.is_record_active ? "Active" : "Inactive"}
                        color={emp.is_record_active ? "success" : "default"}
                        size="small"
                        clickable={false}
                        onClick={() => {}}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" gap={1} justifyContent="flex-end">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() =>
                              navigate(
                                `/people/form?mode=view&id=${emp.id}&type=employee`,
                              )
                            }
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Edit Item">
                          <IconButton
                            size="small"
                            onClick={() =>
                              navigate(
                                `/people/form?mode=edit&id=${emp.id}&type=employee`,
                              )
                            }
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>

                        {!emp.is_record_active ? (
                          <Tooltip title={`Reactivate Employee`}>
                            <IconButton
                              size="small"
                              onClick={() => handleReactivate(emp.id)}
                              color="success"
                              disabled={reactivateEmpMutation.isPending}
                            >
                              <RestoreIcon />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title={`Deactivate Employee`}>
                            <IconButton
                              size="small"
                              onClick={() => handleDeactivate(emp.id)}
                              color="error"
                              disabled={deactivateEmpMutation.isPending}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={totalCount}
          page={pagination.page}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.size}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={paginationDefaults.allRowOptions}
        />
      </TableContainer>
    </Box>
  );
};
